"use client";

import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { createBranchLink, getTopTenStepsList } from "@/api";
import { GlutenFreeTopPlace } from "@/components/organism/GlutenFreeTopPlace";
import { TopPlaceFrame } from "@/components/atoms/TopPlaceFrame";
import { TopPlaceImageSkeleton } from "@/components/atoms/TopPlaceImageSkeleton";
import { TopPlaceInformationSkeleton } from "@/components/atoms/TopPlaceInformationSkeleton";
import { GlutenFreeLeafletMap } from "@/components/organism/GlutenFreeLeafletMap";
import { FormatAmount } from "@/utils";
import { GlutenFreeModal } from "@/components/organism/GlutenFreeModal";
import { useTrackPlaceListScroll } from "@/hooks/useTrackPlaceListScroll";
import { MixpanelEvents } from "@/constants/mixpanel";
import { FeatureFlagKeys, FeatureFlagValues } from "@/constants/ab-tests";
import { getCheckoutSessionUrl, trackTopTenStartSession } from "@/utils/api";
import { Suspense } from 'react';

import {
    track,
    trackViewMenuButtonClick,
    trackReviewsButtonClick,
    trackFooterAppButtonClick,
    trackStartQuizClick,
    trackFooterJoinCommunityButtonClick,
    registerMixpanelProperties,
    getMixpanelDistinctId,
    verifyMixpanelRegistration,
    trackIndirectAdsQuizStart,
} from "@/utils/mixpanel";
import { GlutenFreeFooter } from "@/components/organism/GlutenFreeFooter";
import { OriginalFixedGlutenFreeFooter } from "@/components/organism/OriginalFixedGlutenFreeFooter";
import { DrawerComponent } from "./DrawerComponent";
import { useStripeData } from "@/hooks/useStripeData";


export const StableClientComponentsWrapper = ({
    onlyQuizUI = false,
    openQuizImmediately = false,
    pageId,
    pageTitle,
    placesCount = 0,
    cursor = null,
    isDesktop = false,
    initPlaces = [],
    isMobile = false,
    pageArea,
    determinedArea,
    country,
    region,
    city,
    neighborhood,
    recommendationsCountOnPage = 0,
    mixpanelData = {},
    isServerFetchedAllPlaces = false,
    quizFlowControllerVariation = "",
    ctaTargetTestVariation = "",
    quizFlowDesktopControllerVariation = "",
    priceTestId = "",
    priceId = "",
    quizAiFlowVariations = "",
    isWebCheckout = false,
    immediateFlow,
    trialVariation = false,
    adTrialVariation,
    urlVariation = "",
    priceTestVariation = "",
    couponId = "",
    isDsaCampaign = false,
    flowVersion = "",
    clientType = "",
    quizCheckoutType,
    disableSignup
}) => {
    const mixpanelDataRef = useRef(mixpanelData);
    const fetchedRef = useRef(false);
    const cursorRef = useRef(cursor);
    const [checkoutSessionUrl, setCheckoutSessionUrl] = useState("");
    const pendingRedirectRef = useRef(false);
    const [branchLink, setBranchLink] = useState("");
    const [activePlace, setActivePlace] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [places, setPlaces] = useState(initPlaces);
    const [userId, setUserId] = useState(null);
    const [totalRecommendations, setTotalRecommendations] = useState(recommendationsCountOnPage);
    const { fetch: fetchStripeData, stripeData } = useStripeData(trialVariation, priceId, couponId);

    useTrackPlaceListScroll(places, onlyQuizUI);

    const fetch = useCallback(
        async (cursorValue) => {
            if (isServerFetchedAllPlaces) {
                return;
            }

            const response = await getTopTenStepsList(pageId, {
                cursor: cursorValue,
                limit: 1, // using init cursor
            });

            setTotalRecommendations(
                (prev) =>
                    prev +
                    response.steps.reduce(
                        (acc, item) => acc + item.total_recommendations,
                        0
                    )
            );

            setPlaces((prev) => {
                return [
                    ...prev,
                    ...response.steps.map((step) => {
                        return {
                            ...step,
                            icon: step.gf_identity_data?.header.icon || "UNKNOWN",
                            tags: step.selected_tags,
                            selected_tags: step.selected_tags,
                        };
                    }),
                ];
            });

            if (response.cursor) {
                await fetch(response.cursor);
            } else {
                setIsLoading(false);
            }
        },
        [pageId, cursor, isServerFetchedAllPlaces]
    );

    useEffect(() => {
        if (!onlyQuizUI) {
            window.scrollTo(0, 0);

            const handleScroll = () => {
                if (fetchedRef.current || cancelScroll) {
                    return;
                }

                fetchedRef.current = true;

                fetch(cursorRef.current);
            };

            window.addEventListener("scroll", handleScroll);

            let cancelScroll = false;
            let timeoutId;

            timeoutId = setTimeout(() => {
                cancelScroll = true;

                if (fetchedRef.current) {
                    return;
                }

                fetchedRef.current = true;
                window.removeEventListener("scroll", handleScroll);
                fetch(cursorRef.current);
            }, 5000);

            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener("scroll", handleScroll);
            };
        }
    }, [fetch]);

    useEffect(() => {
        if (!onlyQuizUI) {
            setTimeout(() => {
                const drawer = document.querySelector("#gfe-footer-drawer");
                const footer = document.querySelector("#gfe-top-ten-footer");

                const observer = new IntersectionObserver(
                    ([entry]) => {
                        if (entry.isIntersecting) {
                            drawer.animate([{ transform: "translateY(100%)", opacity: 0 }], {
                                easing: "ease-in-out",
                                duration: 500,
                                iterations: 1,
                                fill: "forwards",
                            });
                        } else {
                            drawer.animate([{ transform: "translateY(0)", opacity: 1 }], {
                                easing: "ease-in-out",
                                duration: 500,
                                iterations: 1,
                                fill: "forwards",
                            });
                        }
                    },
                    { threshold: 0.1 }
                );

                if (footer) {
                    observer.observe(footer);
                }
            }, 1000);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            import("@/utils/branch")
                .then((mod) => {
                    mod.initBranch(
                        async (data) => {
                            const referringLinkUrl = data.referring_link || "";
                            const link = await createBranchLink({
                                id: pageId,
                                branchData: data,
                                referringLinkUrl,
                            });

                            registerMixpanelProperties({
                                ...mixpanelDataRef.current,
                                branchData: data,
                            });

                            setTimeout(verifyMixpanelRegistration, 2000); // Verify after 2 seconds
                            setBranchLink(link);

                            if (onlyQuizUI) {
                                trackIndirectAdsQuizStart();
                                return;
                            }

                            trackStartQuizClick({
                                quizFlowControllerVariation,
                                area: pageArea,
                                count: placesCount,
                                link,
                                isMobile,
                            });
                            trackFooterAppButtonClick(link);
                            trackViewMenuButtonClick(link);
                            trackFooterJoinCommunityButtonClick(link);
                            trackReviewsButtonClick();
                        },
                        (error) => {
                            console.error("Error calling initBranch", error);
                        }
                    );
                })
                .catch((error) => {
                    console.error("Error calling initBranch", error);
                });
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const element = document.getElementById("social-proof-avatars-count");

            if (element) {
                element.textContent = `${FormatAmount(totalRecommendations)}+`;
                element.animate(
                    [
                        { transform: "scale(1)" },
                        { transform: "scale(0.1)" },
                        { transform: "scale(1.2)" },
                        { transform: "scale(1)" },
                    ],
                    {
                        duration: 800,
                        iterations: 1,
                    }
                );

                element.parentElement.classList.remove("animate-pulse");
            }
        }
    }, [isLoading, totalRecommendations]);

    const isNewFlow = useMemo(() =>
        immediateFlow || quizFlowControllerVariation === FeatureFlagValues[FeatureFlagKeys.TT_QUIZ_FLOW_CONTROLLER].C,
        [immediateFlow, quizFlowControllerVariation]
    );

    const aiFlow = useMemo(() =>
        immediateFlow
            ? FeatureFlagValues[FeatureFlagKeys.TT_AI_QUIZ_FLOW_VARIATIONS].E
            : quizAiFlowVariations,
        [immediateFlow, quizAiFlowVariations]
    );

    const flowControllerVariation = useMemo(() =>
        immediateFlow
            ? FeatureFlagValues[FeatureFlagKeys.TT_QUIZ_FLOW_CONTROLLER].C
            : quizFlowControllerVariation,
        [immediateFlow, quizFlowControllerVariation]
    );

    useEffect(() => {
        registerMixpanelProperties({
            stripe_customer_id: stripeData?.customerId,
            stripe_price_amount: stripeData?.priceAmount,
            stripe_price_per_month: stripeData?.pricePerMonth,
            tt_quiz_checkout_type: stripeData?.customerId
                ? "stripe_elements"
                : quizCheckoutType,
        });
    }, [stripeData, quizCheckoutType]);

    useEffect(() => {
        console.log("Stable");
        registerMixpanelProperties(mixpanelData);

        if (onlyQuizUI) {
            fetchStripeData();
            return;
        }

        trackTopTenStartSession();
        fetchStripeData();
    }, []);

    const onSlideAction = useCallback(props => {
        const eventName = onlyQuizUI
            ? MixpanelEvents.INDIRECT_ADS_QUIZ_ACTION
            : MixpanelEvents.TT_QUIZ_ACTION;

        if (!disableSignup && props.page_name.toLowerCase().includes("email")) {
            getCheckoutSessionUrl({
                email: props.value_submitted[0],
                link: branchLink,
                trialVariation: immediateFlow ? adTrialVariation : trialVariation,
                priceTestId,
                priceTest: priceTestVariation,
                couponId,
                pageId,
                clientType,
            }).then(({ sessionUrl, branchLink, userId }) => {
                if (sessionUrl) {
                    setCheckoutSessionUrl(sessionUrl);
                    if (pendingRedirectRef.current) {
                        pendingRedirectRef.current = false;
                        window.location.href = sessionUrl;
                    }
                }

                if (branchLink) {
                    setBranchLink(branchLink);
                }

                if (userId) {
                    setUserId(userId);
                }
            })
                .catch((error) => {
                    console.error(error);
                    track(MixpanelEvents.TT_QUIZ_CHECKOUT_API_ERROR, {
                        checkout_session_url: null,
                        is_web_checkout: isWebCheckout,
                        branch_link: branchLink,
                        error: error.message,
                    });
                });
        }

        track(eventName, {
            ...props,
            checkout_session_url: checkoutSessionUrl,
            is_web_checkout: isWebCheckout,
            branch_link: branchLink,
        });
    }, [
        onlyQuizUI,
        priceTestId,
        priceTestVariation,
        branchLink,
        checkoutSessionUrl,
        isWebCheckout,
        immediateFlow,
        adTrialVariation,
        trialVariation,
        clientType,
        couponId,
        pageId,
    ]);

    const onFormSubmit = useCallback(() => {
        const eventName = onlyQuizUI
            ? MixpanelEvents.INDIRECT_ADS_QUIZ_FORM_SUBMIT
            : MixpanelEvents.TT_QUIZ_FORM_SUBMIT;

        track(eventName, {
            price_test_variation: priceTestVariation,
            link: branchLink,
            checkout_session_url: checkoutSessionUrl,
            is_web_checkout: isWebCheckout,
            trialVariation: immediateFlow ? adTrialVariation : trialVariation,
            priceTestVariation,
        });

        // TODO: branch link open occurs in mixpanel tracking functions
        // this is confusing, we should refactor this
        if (isWebCheckout) {
            if (checkoutSessionUrl) {
                window.location.href = checkoutSessionUrl;
            } else {
                pendingRedirectRef.current = true;
            }
        } else {
            window.location.href = branchLink;
        }
    }, [
        onlyQuizUI,
        adTrialVariation,
        immediateFlow,
        trialVariation,
        branchLink,
        checkoutSessionUrl,
        isWebCheckout,
        pendingRedirectRef,
        priceTestVariation,
    ]);

    if (onlyQuizUI) {
        // TODO: write once
        return (
            <Suspense fallback={null}>
                <DrawerComponent
                    urlVariation={urlVariation}
                    openQuizImmediately={openQuizImmediately}
                    area={determinedArea}
                    country={country}
                    region={region}
                    city={city}
                    flowVersion={flowVersion}
                    quizAiFlowVariations={quizAiFlowVariations}
                    distinctId={getMixpanelDistinctId()}
                    neighborhood={neighborhood}
                    isMobile={isMobile}
                    link={branchLink}
                    quizFlowControllerVariation={flowControllerVariation}
                    aiFlowVariation={aiFlow}
                    isNewFlow={isNewFlow}
                    trialVariation={trialVariation}
                    trackAction={onSlideAction}
                    formSubmit={onFormSubmit}
                    isDsaCampaign={isDsaCampaign}
                    stripeData={stripeData}
                    userId={userId}
                    withTrial={adTrialVariation}
                    branchLink={branchLink}
                    trackView={(props) => {
                        track(MixpanelEvents.INDIRECT_ADS_QUIZ_PAGE_VIEWED, props);
                    }}
                />
            </Suspense>
        );
    }

    return (
        <>
            <div
                id="places_list"
                className="px-4 md:px-10 2xl:px-20 flex flex-col gap-12 overflow-hidden">
                <GlutenFreeModal
                    link={branchLink}
                    area={determinedArea}
                    isMobile={isMobile}
                    places={places}
                    quizFlowControllerVariation={quizFlowControllerVariation}
                    isNewFlow={isNewFlow}
                />
                {places.map((place, index) => {
                    if (!place) {
                        return (
                            <TopPlaceFrame key={index}>
                                <TopPlaceImageSkeleton />
                                <TopPlaceInformationSkeleton />
                            </TopPlaceFrame>
                        );
                    }

                    return (
                        <GlutenFreeTopPlace
                            isMobile={isMobile}
                            position={index + 1}
                            place={place}
                            link={branchLink}
                            placeId={place?._id}
                            key={place?._id || index}
                        />
                    );
                })}
                {isDesktop ? (
                    <GlutenFreeLeafletMap
                        isActive
                        deviceType="desktop"
                        places={[...initPlaces, ...places].filter(Boolean)}
                        // activePlace={response[1].steps[0]}
                        activePlace={activePlace}
                        setActivePlace={setActivePlace}
                    />
                ) : null}
            </div>

            <GlutenFreeFooter
                pageTitle={pageTitle}
                pageArea={determinedArea}
                isMobile={isMobile}
                link={branchLink}
            />
            {(isMobile && isNewFlow) ?
                <DrawerComponent
                    area={determinedArea}
                    country={country}
                    region={region}
                    city={city}
                    flowVersion={flowVersion}
                    quizAiFlowVariations={quizAiFlowVariations}
                    distinctId={getMixpanelDistinctId()}
                    immediateFlow={immediateFlow}
                    neighborhood={neighborhood}
                    isMobile={isMobile}
                    link={branchLink}
                    quizFlowControllerVariation={flowControllerVariation}
                    aiFlowVariation={aiFlow}
                    isNewFlow={isNewFlow}
                    trialVariation={trialVariation}
                    trackAction={onSlideAction}
                    formSubmit={onFormSubmit}
                    isDsaCampaign={isDsaCampaign}
                    stripeData={stripeData}
                    userId={userId}
                    withTrial={trialVariation}
                    branchLink={branchLink}
                    trackView={(props) => {
                        track(MixpanelEvents.TT_QUIZ_PAGE_VIEWED, props);
                    }}
                />
                : <OriginalFixedGlutenFreeFooter
                    branchLink={branchLink}
                    isMobile={isMobile}
                    ctaTargetTestVariation={ctaTargetTestVariation}
                    quizFlowControllerTestVariation={quizFlowDesktopControllerVariation}
                    placesCount={placesCount}
                    area={determinedArea} />}
        </>
    );
};
